import { Action } from "redux";
import _ from "underscore";

import { api } from "../../shared/api/system/apiClient";
import { SharedActions } from "../../shared/flux/actions/sharedActions";
import { CollectionKeyHelper } from "../../shared/helpers/contentCollectionHelpers";
import { ContentTypeEnumHelper } from "@travellocal/utils/enumHelper";
import { LocalStorageHelper } from "@travellocal/utils/localStorageHelper";
import SharedActionPromises from "../../shared/state/actionPromises";
import { StoreActionFactory } from "../../shared/state/storeActionFactory";
import { Actions } from "./actions";
export class ActionPromises extends SharedActionPromises {

  /**
   * Get a page of content and update the redux store.
   * @param filters Bundle of arguments for the /content search api
   * @param page The page number to get. If < 1, then will be set to 1, and the redux store will clear all existing items.
   *        If >= 1, the retrieved page will be appended to the redux store.
   * @param pageSize Size of the page.
   */
  public static getContentPage(
    params: IListContentParams,
    page: number,
    pageSize: number,
    context: { key: string }) {

    const pageNumber = page < 1 ? 1 : page;
    pageSize = pageSize || 20;
    const realContext: Partial<ActionContext> = { ...context, page, pageSize, params };

    return StoreActionFactory.thunk<IWebStoreState>((d, getState) => {
      const { currency } = getState();
      const currencyCode = currency.data ? currency.data.code : undefined;

      return d(StoreActionFactory.api(Actions.GetContentPage, api.getContentPage(params, pageNumber, pageSize, currencyCode), realContext));
    });
  }

  /**
   * Get a page of content and store it in a collection, if there's nothing in that collection.
   */
  public static initialiseContentCollection = (
    key: string,
    filters: IListContentParams,
    page: number,
    pageSize: number,
  ) => {
    return StoreActionFactory.thunk<IWebStoreState>((dispatch, getState) => {
      const state = getState();
      const currentState = state.contentByCollection.data[key];
      const shouldFetch = !currentState || !currentState.data || !currentState.data.length || page < 0;

      return shouldFetch
        ? dispatch(ActionPromises.getContentPage(filters, page, pageSize, { key }))
        : undefined;
    });
  }

  /**
   * Load location section items for a location as a content collection in the redux store.
   */
  public static getLocationSections(slug: string) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      const locationContent = await api.getLocationContent(slug);
      const { id, contentSections } = locationContent.json;

      // Create the payload that the content collection reducer is expecting, saves having another redux key
      const sectionsPayload: IFetchApiResponse<IApiResponse<IContent[]>> = {
        response: { status: 200, isPrimary: false },
        json: { data: contentSections },
      };
      const sectionsContext = { key: CollectionKeyHelper.LocationOverviewSections(id) };

      return dispatch(StoreActionFactory.action(Actions.GetContentPage, sectionsPayload, sectionsContext));
    });
  }

  /**
   * Get some itineraries, reviews, and other content for the country pages.
   */
  public static getContentPagesForCountry(filters: IListContentParams) {
    const page = -1; // Always reset
    const pageSize = filters.pageSize || 20;

    const sampleItinFilterSet = { ...filters, types: [ContentType.SampleItinerary] };
    const reviewsFilterSet = { ...filters, types: [ContentType.Review] };
    const relatedContentFilters = { ...filters, types: [ContentType.BlogPost] };

    const itinContext = { key: CollectionKeyHelper.LocationItineraries(...filters.locations) };
    const reviewsContext = { key: CollectionKeyHelper.LocationReviews(...filters.locations) };
    const relatedContext = { key: CollectionKeyHelper.LocationRelated(...filters.locations) };

    return StoreActionFactory.thunk(dispatch => Promise.all([
      dispatch(this.getContentPage(sampleItinFilterSet, page, pageSize, itinContext)),
      dispatch(this.getContentPage(reviewsFilterSet, page, pageSize, reviewsContext)),
      dispatch(this.getContentPage(relatedContentFilters, -1, 7, relatedContext)),
    ]));
  }

  public static getItinerariesForCountryTripIdeas(filters: IListContentParams): IThunkAction<any> {
    const page = -1; // Always reset
    const pageSize = 20;

    const sampleItinGroupToursFilterSet = { ...filters, isGroupTour: true };
    const sampleItinFeaturedToursFilterSet = { ...filters, isGroupTour: false };

    const itinGroupToursContext = { key: CollectionKeyHelper.LocationItinerariesGroupTours(...filters.locations) };
    const itinTailoredToursContext = { key: CollectionKeyHelper.LocationItinerariesTailoredTours(...filters.locations) };

    return StoreActionFactory.thunk(async (dispatch) => {
      await dispatch(this.getContentPage(sampleItinGroupToursFilterSet, page, pageSize, itinGroupToursContext));
      await dispatch(this.getContentPage(sampleItinFeaturedToursFilterSet, page, pageSize, itinTailoredToursContext));
    });
  }

  /**
   * Get a single content item and update the redux store.
   */
  public static getContentById(reduxKey: string, id: string) {
    return StoreActionFactory.thunk<IWebStoreState>((dispatch, getState) => {
      const { currency } = getState();
      return dispatch(StoreActionFactory.api(reduxKey, api.getContentById(id, currency.data.code)));
    });
  }

  /**
   * Get a page of content that's related to the given piece of content, and update the redux store.
   */
  private static getRelatedContentFromSeed(relatedTo: IContent, count: number) {
    const params = {
      themes: relatedTo.themes ? relatedTo.themes.map((x) => x.id) : [],
      locations: relatedTo.locations ? relatedTo.locations.map((x) => x.id) : [],
    } as IListContentParams;

    const context = { key: CollectionKeyHelper.ContentDetailRelated(relatedTo.id) };
    return this.getContentPage(params, -1, count + 1, context);
  }

  public static getContentAndRelatedById(id: string, relatedCount: number) {
    return StoreActionFactory.thunk<IWebStoreState>((dispatch, getState) => {
      return dispatch(this.getContentById(Actions.GetContentSingle, id))
        .then((response) => {
          const state = getState();
          if (state.contentItem.data && state.contentItem.status === "OK") {
            return dispatch(this.getRelatedContentFromSeed(state.contentItem.data, relatedCount));
          }
        });
    });
  }

  public static getContentForDropdowns() {
    return StoreActionFactory.thunk<IWebStoreState>((dispatch) => {
      const overallRating: INumericRange = {
        type: NumericRangeType.GreaterThanOrEqual,
        min: 4,
      };

      const actions = [
        ActionPromises.getContentPage({ types: [ContentType.Review], overallRating }, -1, 1, {
          key: CollectionKeyHelper.DropDownContentReviews
        }),
        ActionPromises.getContentPage({ types: [ContentType.SampleItinerary] }, -1, 1, {
          key: CollectionKeyHelper.DropDownContentItineraries
        }),
        ActionPromises.getContentPage({ types: [ContentType.BlogPost] }, -1, 1, {
          key: CollectionKeyHelper.DropDownContentArticles
        })
      ];

      return Promise.all(actions.map(action => dispatch(action)));
    });
  }

  /**
   * Handles old-style /blog/:slug route.
   * @param slug The slug of the blog post.
   */
  public static blogPostBySlug = (slug: string) => StoreActionFactory.api(Actions.GetContentSingle, api.getBlogPostBySlug(slug));

  public static imageByFileName(filename: string) {
    return StoreActionFactory.api(Actions.GetImageByFileName, api.getImageByFileName(filename));
  }

  public static changeCurrency(currency: IBaseCurrency) {
    return StoreActionFactory.action(Actions.ChangeCurrency, currency);
  }

  public static allCustomerCurrencies() {
    return StoreActionFactory.thunk<IWebStoreState>((d, s) => {
      return d(StoreActionFactory.api(Actions.LoadAllCurrencies, api.getCustomerCurrencies()))
        .then(() => {
          // If there's no currency set, set one - will have been read into the initial state before render by browser.tsx
          const store = s();
          if (store.currencies.data && !store.currency.data) {
            const defaultCurrency = store.currencies.data.filter((x) => x.code === "GBP")[0];
            return d(ActionPromises.changeCurrency(defaultCurrency));
          }
        });
    });
  }
  public static allRegions() {
    return StoreActionFactory.api(Actions.GetAllRegions, api.getLocations({ locationType: LocationType.Region, pageSize: 100 }));
  }

  public static allCountries() {
    return StoreActionFactory.api(Actions.GetAllCountries, api.getLocations({ locationType: LocationType.Country, pageSize: 100 }));
  }

  public static getFeaturedLocations(ids: number[]) {
    return StoreActionFactory.api(Actions.GetFeaturedLocations, api.getLocations({ includeIds: ids }));
  }

  public static allAgeRanges = () => StoreActionFactory.api(Actions.GetAllAgeRanges, api.getAllAgeRanges());

  public static inspirationThemes = (pageSize: number) => {
    return StoreActionFactory.api(Actions.GetInspirationThemes, api.getThemePage(-1, pageSize, { inspiration: true }), { pageSize, page: 1 });
  }

  public static enquiryThemes = (pageSize: number) => {
    return StoreActionFactory.api(Actions.GetEnquiryThemes, api.getThemePage(-1, pageSize, { enquiry: true }), { pageSize, page: 1 });
  }

  public static getTheme = (id: number) => StoreActionFactory.api(Actions.GetContentTheme, api.getThemeById(id));

  public static allCardTypes() {
    return StoreActionFactory.api(Actions.GetAllCardTypes, api.getCardTypes());
  }

  public static regionBySlug(slug: string, destinationType: LocationType, isPrimary?: boolean) {
    return StoreActionFactory.api(Actions.GetRegionBySlug, api.getLocationBySlug(slug, destinationType, isPrimary));
  }

  public static countryBySlug(slug: string, isPrimary?: boolean) {
    return StoreActionFactory.api(Actions.GetCountryBySlug, api.getLocationBySlug(slug, null, isPrimary));
  }

  public static countryAndDefaultLtoFromSlug(locationSlug: string, isPrimary?: boolean) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.GetCountryBySlug, api.getLocationBySlug(locationSlug, null, isPrimary)))
        .then(() => {
          const state = s() as IWebStoreState;
          if (state.country.status === "OK" && state.country.data && state.country.data.defaultLto) {
            return d(ActionPromises.ltoByIdOrSlug(state.country.data.defaultLto.id));
          }
          else {
            // Clear the store if there's no lto for this country - show "Coming soon"
            return d(StoreActionFactory.action(Actions.GetLtoById, null, { context: "no default lto" }));
          }
        });
    });
  }

  public static destinationBySlug(locationSlug: string) {
    return StoreActionFactory.api(Actions.GetDestinationBySlug, api.getLocationBySlug(locationSlug));
  }

  public static ltoByIdOrSlug(slugOrId: number | string) {
    return StoreActionFactory.api(Actions.GetLtoBySlug, api.getLtoByIdOrSlug(slugOrId));
  }

  public static ltoPrimaryLocationBySlug(slug: string) {
    return StoreActionFactory.api(Actions.GetLtoPrimaryLocationBySlug, api.getLocationBySlug(slug));
  }

  /**
   * Get all content required for /:ltoSlug/
   */
  public static initialiseLtoDetail(slugOrId: string) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      let state = getState();
      if (!state.lto.data || !(state.lto.data.slug === slugOrId || state.lto.data.id === parseInt(slugOrId, 10))) {
        await dispatch(this.ltoByIdOrSlug(slugOrId));
      }

      state = getState();
      const lto = state.lto.data;
      if (!lto) {
        return;
      }

      const actions = [

        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.LtoItineraries(lto.id),
          { ltos: [lto.id], types: [ContentType.SampleItinerary] },
          1,
          100, // Show all itineraries - TODO add paging
        ),

        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.LtoReviews(lto.id),
          { ltos: [lto.id], types: [ContentType.Review] },
          1,
          100, // Show all reviews - TODO add paging
        ),

        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.LtoOverviewRelated(lto.id),
          {
            locations: lto.locations.map(x => x.id),
            types: _.without(ContentTypeEnumHelper.asArray(), ContentType.SampleItinerary, ContentType.Review),
          },
          1,
          3,
        ),
      ];

      return actions.map(action => dispatch(action));
    });
  }

  /**
   * Get all content required for /:countrySlug/reviews
   */
  public static initialiseCountryReviewArchive(slug: string) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      let state = getState();
      if (!state.country.data || state.country.data.slug !== slug) {
        await dispatch(this.countryBySlug(slug));
      }

      state = getState();
      if (!state.country.data) {
        return;
      }

      const countryId = state.country.data.id;

      const actions = [

        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.LocationReviews(countryId),
          { locations: [countryId], types: [ContentType.Review] },
          1,
          100, // Show all reviews - TODO add paging
        ),

        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.LocationReviewsRelated(countryId),
          {
            locations: [countryId],
            types: _.without(ContentTypeEnumHelper.asArray(), ContentType.Review),
          },
          1,
          3,
        ),
      ];

      return actions.map(action => dispatch(action));
    });
  }

  /**
   * Get all content required for /inspiration/review/:contentId
   * @param contentId Inspiration id of a review - not the review id
   */
  public static initialiseReviewDetail(contentId: number) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      let state = getState();
      if (!state.contentItem.data || state.contentItem.data.id !== contentId) {
        await dispatch(this.getContentById(Actions.GetContentSingle, String(contentId)));
      }

      state = getState();
      if (!state.contentItem.data) {
        return;
      }

      const actions: Action[] = [
        // Get some related reviews for the same location(s)
        ActionPromises.initialiseContentCollection(
          CollectionKeyHelper.ContentDetailRelated(contentId),
          {
            locations: state.contentItem.data.locations.map(x => x.id),
            types: [ContentType.Review],
          },
          1,
          4,
        ),
      ];

      // #1737 images aren't returned in locationSummaries anymore - have to choose a primary location
      // to get its review footer image
      const [primaryLocation] = state.contentItem.data.locations;
      if (primaryLocation) {
        actions.push(ActionPromises.countryBySlug(primaryLocation.slug));
      }

      return actions.map(action => dispatch(action));
    });
  }

  public static ltoTermsBySlug(slugOrId: string) {
    return StoreActionFactory.thunk<IWebStoreState>(async (d, s) => {
      const state1 = s();
      if (!state1.lto.data || !(state1.lto.data.slug === slugOrId || state1.lto.data.id === parseInt(slugOrId, 10))) {
        await d(this.ltoByIdOrSlug(slugOrId));
      }

      const state2 = s();
      if (state2.lto.data) {
        return d(StoreActionFactory.api(Actions.GetLtoTermsById, api.getLtoTerms(state2.lto.data.id)));
      }
    });
  }

  public static clearCustomerEnquiry() {
    return StoreActionFactory.action(Actions.ClearCurrentEnquiry, null);
  }

  public static customerEnquiry(enquiryId?: number, updateEnqItin?: boolean) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      await dispatch(StoreActionFactory.api(Actions.GetEnquiryById, api.getEnquiry(enquiryId)));
      const state = getState();
      const lto = state.lto.data;
      const enquiry = state.customerEnquiry.data;

      await Promise.all([
        // Get enquiry LTO if needed
        enquiry.isConnected && (!lto || lto.id !== enquiry.lto.id)
          ? dispatch(this.ltoByIdOrSlug(enquiry.lto.id))
          : undefined,

        // Get enquiry location
        enquiry.location && enquiry.location.slug
          ? dispatch(this.countryBySlug(enquiry.location.slug))
          : undefined,
      ]);

      // We need the *current* itinerary when the tab loads, so the correct visa links show in FAQs and on booked card
      if (updateEnqItin && enquiry.currentQuoteId != null) {
        const quoteId = enquiry.currentQuoteId;
        await dispatch(StoreActionFactory.api(Actions.GetEnquiryItinerary, api.getItineraryByQuote(quoteId), { key: quoteId }));
      }
    });
  }

  public static ltosByLocationSlug(locationSlug: string) {
    return StoreActionFactory.api(Actions.GetLtosByLocationSlug, api.getLtosByLocationSlug(locationSlug));
  }

  public static destinationsByCountrySlug(countrySlug: string) {
    return StoreActionFactory.api(Actions.GetDestinationsByCountrySlug, api.getDestinationsByCountrySlug(countrySlug));
  }

  public static countriesByRegionId(regionSlug: string, isPrimary?: boolean) {
    return StoreActionFactory.api(Actions.GetCountriesByRegionSlug, api.getCountriesByRegionSlug(regionSlug, isPrimary));
  }

  public static enquiryById(enquiryId: number) {
    return StoreActionFactory.api(Actions.GetEnquiryById, api.getEnquiry(enquiryId));
  }

  public static enquiryPaymentsByEnquiryId(enquiryId: number, statuses?: PaymentStatus[]) {
    return StoreActionFactory.api(Actions.GetEnquiryPayments, api.getPaymentsForEnquiry(enquiryId, statuses));
  }

  public static allEnquiriesForCustomer() {
    return StoreActionFactory.api(Actions.GetEnquiriesForCustomer, api.getEnquiriesForCustomer());
  }

  public static markItineraryViewed(quoteId: number) {
    return StoreActionFactory.api(Actions.MarkItineraryViewed, api.markItineraryViewed(quoteId));
  }

  public static enquiryAndCurrentQuoteById(enquiryId: number) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {
      await dispatch(ActionPromises.enquiryById(enquiryId));

      const state = getState();
      const enquiry = state.customerEnquiry.data;
      if (enquiry && enquiry.isConnected) {
        return dispatch(ActionPromises.enquiryItineraryQuoteById(enquiry.currentQuoteId));
      }
    });
  }

  public static enquiryItineraryQuoteById(quoteId: number) {
    return StoreActionFactory.thunk<IWebStoreState>(async (dispatch, getState) => {

      const state = getState();
      const enquiry = state.customerEnquiry;
      if (enquiry && state.customerItineraries.data && state.customerItineraries.data[quoteId]) {
        // No need to fetch a quote if we already have it - they don't change.
        return;
      }

      return dispatch(StoreActionFactory.api(Actions.GetEnquiryItinerary, api.getItineraryByQuote(quoteId), { key: quoteId }));
    });
  }

  public static sampleItineraryById(itinerarySlug: string, currencyCode: string) {
    return StoreActionFactory.api(Actions.GetSampleItineraryBySlug, api.getSampleItinerary(itinerarySlug, currencyCode));
  }

  public static requestCancellation(cancellationRequest: IEnquiryCancellationRequestUpdate) {
    return StoreActionFactory.api(Actions.RequestEnquiryCancellation, api.requestCancellation(cancellationRequest));
  }

  public static login(loginRequest: ILoginRequest) {
    const promise = api.login(loginRequest).then((loginResponse) => {
      LocalStorageHelper.setItem("authtoken", loginResponse.json.token);

      return api.getProfile().then((profile) => {
        const login: ILoginResponse = {
          ...loginResponse.json,
          user: profile.json.data,
        };

        return login;
      });
    });

    return StoreActionFactory.action<Promise<ILoginResponse>>(SharedActions.Authentication.Login, promise, { from: "Login request" });
  }

  public static loginWithToken(token: string) {
    LocalStorageHelper.setItem("authtoken", token);

    const promise = api.getProfile().then((profile) => {
      const login: ILoginResponse = {
        token, // TODO no expiry available here
        user: profile.json.data,
      };

      return login;
    });

    return StoreActionFactory.action<Promise<ILoginResponse>>(SharedActions.Authentication.Login, promise, { from: "With token" });
  }

  public static forgotPassword(input: IForgottenPasswordRequest) {
    return StoreActionFactory.api(SharedActions.Authentication.ForgottenPassword, api.forgotPassword(input));
  }

  public static resetPassword(input: IResetPasswordRequest) {
    return StoreActionFactory.api(SharedActions.Authentication.CompleteForgottenPassword, api.resetPassword(input));
  }

  public static logout() {
    return StoreActionFactory.action(SharedActions.Authentication.Logout, true);
  }

  public static excursionsByLtoIdAndCountryId(ltoId: number, countryId: number) {
    return StoreActionFactory.api(Actions.GetExcursionsByLtoIdAndCountryId, api.getExcursionsByLtoIdAndCountryId(ltoId, countryId));
  }

  public static addNewTravelerDetails(newTravelerDetails: INewTravelerDetailsInput) {
    return StoreActionFactory.api(Actions.AddNewTravelerDetails, api.addNewTravelerDetails(newTravelerDetails));
  }

  public static updateTravelerDetails(travelerDetails: Partial<ITravelerDetails>) {
    return StoreActionFactory.api(Actions.UpdateTravelerDetails, api.updateTravelerDetails(travelerDetails));
  }

  public static addNewLeadTravelerDetails(travelerDetails: INewTravelerDetailsInput) {
    return StoreActionFactory.api(Actions.AddNewLeadTravelerDetails, api.addNewLeadTravelerDetails(travelerDetails));
  }

  public static updateLeadTravelerDetails(enquiryId: number, travelerDetails: Partial<ILeadTravelerDetails>) {
    const newInput = { enquiryId, ...travelerDetails };

    return StoreActionFactory.api(Actions.UpdateLeadTravelerDetails, api.updateLeadTravelerDetails(newInput));
  }

  public static deleteTravelerDetails(travelerId: number) {
    return StoreActionFactory.api(Actions.DeleteTravelerDetails, api.deleteTravelerDetails(travelerId));
  }

  public static createReview(review: IReviewInput) {
    return StoreActionFactory.api(Actions.CreateReview, api.createReview(review));
  }

  public static teamProfiles() {
    return StoreActionFactory.api(Actions.GetSettings, api.getTeamProfiles(), { key: "teamProfiles" });
  }
}
